import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import ClassList from '../ClassList';
import ClassDetail from '../ClassDetail';
import ClassScheduleManager from '@/components/schedule/ClassScheduleManager';
import AttendanceTracker from './AttendanceTracker';
import FilterBar from './FilterBar';
import { Class } from '@/types/classTypes';
import { motion } from 'framer-motion';
import { StandardPageHeader } from '@/components/layout/StandardPageHeader';
import { CalendarRange, Users, ListChecks } from 'lucide-react';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const ClassManagementDashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  
  // State
  const [classes, setClasses] = useState<Class[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  // Get URL params
  const classId = searchParams.get('classId');
  const activeTab = searchParams.get('tab') || 'classes';
  
  // Default to demo organization if user metadata is not available
  const organizationId = user?.user_metadata?.organization_id || '00000000-0000-0000-0000-000000000001';

  // Filters
  const [seasonId, setSeasonId] = useState<string | null>(searchParams.get('seasonId'));
  const [visibilityFilter, setVisibilityFilter] = useState<string>(searchParams.get('visibility') || 'all');
  // Keep instructorId for backward compatibility but don't use it in filtering
  const [instructorId, setInstructorId] = useState<string | null>(searchParams.get('instructorId'));

  useEffect(() => {
    fetchClasses();
  }, [seasonId, visibilityFilter, organizationId]);

  const fetchClasses = async () => {
    setLoading(true);
    try {
      // Use supabase query builder directly instead of service
      let query = supabase
        .from('classes')
        .select(`
          *,
          locations (
            id,
            name
          )
        `)
        .eq('organization_id', organizationId);
      
      // Apply season filter if provided
      if (seasonId && seasonId !== 'all') {
        query = query.eq('season_id', seasonId);
      }
      
      // Apply visibility filter
      if (visibilityFilter === 'visible') {
        query = query.eq('is_active', true);
      } else if (visibilityFilter === 'hidden') {
        query = query.eq('is_active', false);
      }
      // If visibilityFilter is 'all', don't add any is_active filter
      
      const { data, error: queryError } = await query;
      
      if (queryError) throw queryError;
      
      setClasses(data || []);
    } catch (err) {
      console.error('Error fetching classes:', err);
      setError(err instanceof Error ? err : new Error('Failed to load classes'));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', value);
    setSearchParams(newParams);
  };

  const handleFilterChange = (type: string, value: string | null) => {
    const newParams = new URLSearchParams(searchParams);
    
    if (value) {
      newParams.set(type, value);
    } else {
      newParams.delete(type);
    }
    
    setSearchParams(newParams);
    
    // Update local state
    switch (type) {
      case 'seasonId':
        setSeasonId(value);
        break;
      case 'visibility':
        setVisibilityFilter(value || 'all');
        break;
      case 'instructorId':
        // Keep this for backward compatibility but it won't affect filtering
        setInstructorId(value);
        break;
    }
  };

  return (
    <div className="space-y-6">
      <StandardPageHeader
        title="Class Management"
        subtitle="Create, schedule, and manage your classes"
        icon={Users}
      />
      
      <motion.div 
        className="space-y-6 p-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div variants={itemVariants}>
          <FilterBar 
            seasonId={seasonId}
            instructorId={instructorId}
            visibilityFilter={visibilityFilter}
            onFilterChange={handleFilterChange}
          />
        </motion.div>

        <motion.div variants={itemVariants}>
          <Card className="border border-gray-100/60 shadow-sm">
            <CardContent className="p-0">
              <Tabs 
                value={activeTab || 'classes'} 
                onValueChange={handleTabChange} 
                className="w-full"
              >
                <TabsList className="w-full justify-start border-b rounded-none gap-2 px-4 py-1">
                  <TabsTrigger value="classes" className="data-[state=active]:bg-primary/10">
                    <Users className="h-4 w-4 mr-2" />
                    Classes
                  </TabsTrigger>
                  <TabsTrigger value="schedule" className="data-[state=active]:bg-primary/10">
                    <CalendarRange className="h-4 w-4 mr-2" />
                    Schedule
                  </TabsTrigger>
                  <TabsTrigger value="attendance" className="data-[state=active]:bg-primary/10">
                    <ListChecks className="h-4 w-4 mr-2" />
                    Attendance
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="classes" className="mt-0">
                  {classId ? (
                    <ClassDetail
                      classId={classId}
                      organizationId={organizationId}
                      loading={loading}
                      error={error || undefined}
                    />
                  ) : (
                    <ClassList 
                      classes={classes}
                      loading={loading}
                      error={error}
                      organizationId={organizationId}
                    />
                  )}
                </TabsContent>
                
                <TabsContent value="schedule" className="mt-0 p-4">
                  <ClassScheduleManager 
                    organizationId={organizationId} 
                    initialClassId={classId}
                  />
                </TabsContent>
                
                <TabsContent value="attendance" className="mt-0 p-4">
                  <AttendanceTracker 
                    organizationId={organizationId}
                    classId={classId}
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ClassManagementDashboard; 